import { useState, useRef, useCallback, useEffect } from 'react';

// ── Types ──
export interface GameTx {
  id: string;
  txid: string;       // real txid
  address: string;     // sender address (chars used for visual)
  x: number;
  y: number;
  speed: number;
  size: 'small' | 'medium' | 'large' | 'xl';
  vsize: number;
  feeRate: number;
  valueSats: number;
  score: number;
  width: number;       // visual width in px
  chars: string[];     // address chars for rendering
  captured: boolean;
  fadeOut: number;      // 0 = visible, >0 = fading
}

export type GameRank = 'Pleb' | 'Stacker' | 'Strong Hand' | 'Block Hunter' | 'Flow Whale' | "Miner's Favorite";

export interface GameResult {
  score: number;
  capturedPct: number;
  rank: GameRank;
  blockHeight: number;
  txCaptured: number;
  txTotal: number;
  satsCaptured: number;
  blockBonus: boolean;
}

export type GamePhase = 'idle' | 'intro' | 'playing' | 'ending' | 'result';

// ── Real tx from mempool.space ──
interface RawMempoolTx {
  txid: string;
  fee: number;
  vsize: number;
  value: number;
}

// ── Helpers ──

function classifySize(vsize: number): GameTx['size'] {
  if (vsize < 250) return 'small';
  if (vsize < 800) return 'medium';
  if (vsize < 2500) return 'large';
  return 'xl';
}

function scoreForSize(s: GameTx['size']): number {
  switch (s) {
    case 'small': return 1;
    case 'medium': return 3;
    case 'large': return 7;
    case 'xl': return 12;
  }
}

function widthForSize(s: GameTx['size']): number {
  switch (s) {
    case 'small': return 3;
    case 'medium': return 5;
    case 'large': return 8;
    case 'xl': return 12;
  }
}

function getRank(pct: number): GameRank {
  if (pct >= 50) return "Miner's Favorite";
  if (pct >= 35) return 'Flow Whale';
  if (pct >= 22) return 'Block Hunter';
  if (pct >= 12) return 'Strong Hand';
  if (pct >= 5) return 'Stacker';
  return 'Pleb';
}

async function fetchRecentTxs(): Promise<RawMempoolTx[]> {
  try {
    const res = await fetch('https://mempool.space/api/mempool/recent');
    if (!res.ok) return [];
    const txs: RawMempoolTx[] = await res.json();
    return txs.slice(0, 10);
  } catch {
    return [];
  }
}

async function fetchTxAddress(txid: string): Promise<string> {
  try {
    const res = await fetch(`https://mempool.space/api/tx/${txid}`);
    if (!res.ok) return txid;
    const data = await res.json();
    return data.vin?.[0]?.prevout?.scriptpubkey_address || txid;
  } catch {
    return txid;
  }
}

const MAX_ROUND_SEC = 60;
const CAPTURE_RADIUS = 34;

export function useStackSatsGame(
  congestion: number,
  blockJustFound: boolean,
  blockHeight: number,
) {
  const [phase, setPhase] = useState<GamePhase>('idle');
  const [score, setScore] = useState(0);
  const [capturedWeight, setCapturedWeight] = useState(0);
  const [totalWeight, setTotalWeight] = useState(0);
  const [txCaptured, setTxCaptured] = useState(0);
  const [txTotal, setTxTotal] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState<GameResult | null>(null);
  const [captures, setCaptures] = useState<{ x: number; y: number; score: number; id: string }[]>([]);

  const txsRef = useRef<GameTx[]>([]);
  const buddyPosRef = useRef({ x: 0, y: 0 });
  const gameActiveRef = useRef(false);
  const startTimeRef = useRef(0);
  const spawnTimerRef = useRef(0);
  const blockHeightAtStartRef = useRef(0);
  const scoreRef = useRef(0);
  const capturedWeightRef = useRef(0);
  const totalWeightRef = useRef(0);
  const txCapturedRef = useRef(0);
  const txTotalRef = useRef(0);
  const satsCapturedRef = useRef(0);

  // Pool of real txs to spawn from, refreshed periodically
  const txPoolRef = useRef<{ tx: RawMempoolTx; address: string }[]>([]);
  const txPoolIndexRef = useRef(0);
  const fetchTimerRef = useRef(0);

  // Fetch real txs with addresses
  const refreshTxPool = useCallback(async () => {
    const txs = await fetchRecentTxs();
    if (txs.length === 0) return;
    // Fetch addresses in parallel (first 10)
    const addresses = await Promise.allSettled(
      txs.map(tx => fetchTxAddress(tx.txid))
    );
    const pool: { tx: RawMempoolTx; address: string }[] = [];
    for (let i = 0; i < txs.length; i++) {
      const addr = addresses[i].status === 'fulfilled' ? (addresses[i] as PromiseFulfilledResult<string>).value : txs[i].txid;
      pool.push({ tx: txs[i], address: addr });
    }
    txPoolRef.current = pool;
    txPoolIndexRef.current = 0;
  }, []);

  const startGame = useCallback(() => {
    setPhase('intro');
    // Pre-fetch tx pool during intro
    refreshTxPool();
    setTimeout(() => {
      txsRef.current = [];
      scoreRef.current = 0;
      capturedWeightRef.current = 0;
      totalWeightRef.current = 0;
      txCapturedRef.current = 0;
      txTotalRef.current = 0;
      satsCapturedRef.current = 0;
      setScore(0);
      setCapturedWeight(0);
      setTotalWeight(0);
      setTxCaptured(0);
      setTxTotal(0);
      setElapsed(0);
      setResult(null);
      setCaptures([]);
      startTimeRef.current = Date.now();
      fetchTimerRef.current = Date.now();
      blockHeightAtStartRef.current = blockHeight;
      gameActiveRef.current = true;
      setPhase('playing');
    }, 1800);
  }, [blockHeight, refreshTxPool]);

  // Capture all uncaptured on-screen txs (used on block found bonus)
  const captureAllOnScreen = useCallback(() => {
    const bonus: { x: number; y: number; score: number; id: string }[] = [];
    for (const tx of txsRef.current) {
      if (!tx.captured) {
        tx.captured = true;
        scoreRef.current += tx.score;
        capturedWeightRef.current += tx.score;
        txCapturedRef.current += 1;
        satsCapturedRef.current += tx.valueSats;
        bonus.push({ x: tx.x, y: tx.y, score: tx.score, id: tx.id });
      }
    }
    if (bonus.length > 0) {
      setScore(scoreRef.current);
      setCapturedWeight(capturedWeightRef.current);
      setTxCaptured(txCapturedRef.current);
      setCaptures(prev => [...prev, ...bonus].slice(-12));
    }
  }, []);

  const endGame = useCallback((blockBonus = false) => {
    if (!gameActiveRef.current) return;

    // Block found bonus: capture everything on screen first
    if (blockBonus) {
      captureAllOnScreen();
    }

    gameActiveRef.current = false;
    setPhase('ending');

    const totalW = totalWeightRef.current;
    const captW = capturedWeightRef.current;
    const pct = totalW > 0 ? (captW / totalW) * 100 : 0;

    const res: GameResult = {
      score: scoreRef.current,
      capturedPct: Math.round(pct * 10) / 10,
      rank: getRank(pct),
      blockHeight: blockHeightAtStartRef.current,
      txCaptured: txCapturedRef.current,
      txTotal: txTotalRef.current,
      satsCaptured: satsCapturedRef.current,
      blockBonus,
    };
    setResult(res);

    setTimeout(() => setPhase('result'), 600);
  }, [captureAllOnScreen]);

  // End on block found during game — with bonus capture
  useEffect(() => {
    if (phase === 'playing' && blockJustFound && blockHeight > blockHeightAtStartRef.current) {
      endGame(true);
    }
  }, [blockJustFound, blockHeight, phase, endGame]);

  const dismiss = useCallback(() => {
    setPhase('idle');
    setResult(null);
    gameActiveRef.current = false;
  }, []);

  // Game tick — called from the game component's rAF
  const tick = useCallback((playAreaW: number, playAreaH: number) => {
    if (!gameActiveRef.current) return txsRef.current;

    const now = Date.now();
    const elapsedSec = (now - startTimeRef.current) / 1000;
    setElapsed(elapsedSec);

    // Time cap
    if (elapsedSec >= MAX_ROUND_SEC) {
      endGame();
      return txsRef.current;
    }

    // Refresh tx pool every 8 seconds
    if (now - fetchTimerRef.current > 8000) {
      fetchTimerRef.current = now;
      refreshTxPool();
    }

    // Spawn rate based on congestion
    const baseInterval = 500 - (congestion / 100) * 300; // 500ms (calm) → 200ms (hot)
    const spawnInterval = Math.max(150, baseInterval);

    if (now - spawnTimerRef.current > spawnInterval) {
      spawnTimerRef.current = now;

      const pool = txPoolRef.current;
      if (pool.length > 0) {
        // Pick next real tx from pool (cycle through)
        const entry = pool[txPoolIndexRef.current % pool.length];
        txPoolIndexRef.current++;

        const rawTx = entry.tx;
        const address = entry.address;
        const feeRate = rawTx.vsize > 0 ? rawTx.fee / rawTx.vsize : 1;
        const size = classifySize(rawTx.vsize);

        // Speed based on fee rate (higher fee = faster drop)
        const feeNorm = Math.min(1, Math.max(0, (feeRate - 1) / 30));
        const speed = 1.0 + feeNorm * 3.5;

        // Build chars from address
        const tailLen = Math.max(4, Math.min(20, Math.floor(rawTx.vsize / 150)));
        const chars: string[] = [];
        for (let i = 0; i < tailLen; i++) {
          chars.push(address[i % address.length]);
        }

        const tx: GameTx = {
          id: `${now}-${rawTx.txid.slice(0, 6)}`,
          txid: rawTx.txid,
          address,
          x: 10 + Math.random() * (playAreaW - 20),
          y: -20,
          speed,
          size,
          vsize: rawTx.vsize,
          feeRate: Math.round(feeRate * 10) / 10,
          valueSats: rawTx.value,
          score: scoreForSize(size),
          width: widthForSize(size),
          chars,
          captured: false,
          fadeOut: 0,
        };

        txsRef.current.push(tx);
        totalWeightRef.current += tx.score;
        txTotalRef.current += 1;
        setTotalWeight(totalWeightRef.current);
        setTxTotal(txTotalRef.current);
      }
    }

    // Move txs & check collision
    const bx = buddyPosRef.current.x;
    const by = buddyPosRef.current.y;
    const newCaptures: typeof captures = [];

    for (const tx of txsRef.current) {
      if (tx.captured) {
        tx.fadeOut += 1;
        continue;
      }
      tx.y += tx.speed;

      // Collision check
      const dx = Math.abs(tx.x - bx);
      const dy = Math.abs(tx.y - by);
      if (dx < CAPTURE_RADIUS && dy < CAPTURE_RADIUS) {
        tx.captured = true;
        scoreRef.current += tx.score;
        capturedWeightRef.current += tx.score;
        txCapturedRef.current += 1;
        satsCapturedRef.current += tx.valueSats;
        setScore(scoreRef.current);
        setCapturedWeight(capturedWeightRef.current);
        setTxCaptured(txCapturedRef.current);
        newCaptures.push({ x: tx.x, y: tx.y, score: tx.score, id: tx.id });
      }
    }

    if (newCaptures.length > 0) {
      setCaptures(prev => [...prev, ...newCaptures].slice(-8));
    }

    // Remove off-screen or fully faded
    txsRef.current = txsRef.current.filter(
      tx => tx.y < playAreaH + 30 && tx.fadeOut < 20,
    );

    return txsRef.current;
  }, [congestion, endGame, refreshTxPool]);

  const updateBuddyPos = useCallback((x: number, y: number) => {
    buddyPosRef.current = { x, y };
  }, []);

  const capturedPct = totalWeight > 0 ? (capturedWeight / totalWeight) * 100 : 0;

  return {
    phase,
    score,
    capturedPct: Math.round(capturedPct * 10) / 10,
    elapsed,
    result,
    captures,
    txs: txsRef,
    startGame,
    endGame,
    dismiss,
    tick,
    updateBuddyPos,
    maxRoundSec: MAX_ROUND_SEC,
  };
}
